import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CornerDownRight, FileText, FileWarning, MousePointerClick, RefreshCw } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { fetchSeoSummary, SEO_STR as S, type SeoSummary } from './seoApi'

/**
 * A12.01 — SEO panель (dashboard). LIVE: `fetchSeoSummary` redirects/404/robots
 * endpoint'laridan yig'ma ko'rsatkichlarni oladi (fail-soft → nol). KPI kartalar +
 * tegishli boshqaruv ekranlariga tez o'tish. Placeholder huquq — sahifa faqat
 * yig'ma o'qish (mutatsiya yo'q).
 */
function Kpi({
  icon,
  label,
  value,
  to,
}: {
  icon: React.ReactNode
  label: string
  value: string
  to: string
}) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-2 rounded-lg border border-border-default bg-surface-2 p-4 hover:border-border-strong"
    >
      <span className="flex items-center gap-2 text-xs text-tertiary">
        {icon}
        {label}
      </span>
      <span className="text-2xl font-semibold text-primary">{value}</span>
    </Link>
  )
}

export function SeoDashboardScreen() {
  const [data, setData] = useState<SeoSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    fetchSeoSummary(ac.signal)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [nonce])

  const num = (n: number | undefined) => (loading || n == null ? '—' : String(n))

  return (
    <>
      <PageHeader
        title={S.dashboard}
        screenId="A12.01"
        actions={
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <RefreshCw size={13} />
            {S.refresh}
          </button>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Kpi
            icon={<CornerDownRight size={13} />}
            label={S.redirects}
            value={num(data?.redirects)}
            to="/seo/redirects"
          />
          <Kpi
            icon={<FileWarning size={13} />}
            label={S.notFound}
            value={num(data?.notFound)}
            to="/seo/not-found"
          />
          <Kpi
            icon={<MousePointerClick size={13} />}
            label={S.kpi404Hits}
            value={num(data?.notFoundHits)}
            to="/seo/not-found"
          />
          <Kpi
            icon={<FileText size={13} />}
            label={S.robotsState}
            value={loading ? '—' : data?.robotsPresent ? S.present : S.absent}
            to="/seo/robots"
          />
        </div>
      </div>
    </>
  )
}
